import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth.js';
import { ApiError } from '../lib/api.js';

/**
 * Registration.
 *
 * Three doors open this form:
 *   1. First-owner bootstrap — no accounts exist yet.
 *   2. `ALLOW_OPEN_REGISTRATION=1` env — anyone can register.
 *   3. A `?invite=<token>` query param from an OWNER's shared link.
 *
 * If an invite is present it's forwarded to the API, which validates
 * and consumes it as part of the register call.
 */
export function RegisterPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const invite = params.get('invite') ?? undefined;
  const { register } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      // AuthProvider.register saves the returned tokens and flips
      // the auth state to signed-in.
      await register(email, password, invite);
      nav('/', { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Registration failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto mt-24 max-w-sm px-4">
      <h1 className="mb-2 text-2xl font-semibold">
        {invite ? 'Accept invitation' : 'Create owner account'}
      </h1>
      <p className="mb-6 text-sm text-slate-500">
        {invite
          ? 'Register a new owner account — you will only see your own properties, tenants, and finances.'
          : 'Every account is its own owner with fully isolated data. Sign in from any device to see only your buildings, tenants, and finances.'}
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        <input
          type="email" required autoComplete="username"
          placeholder="Email"
          value={email} onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
        />
        <input
          type="password" required autoComplete="new-password" minLength={12}
          placeholder="Password (12+ characters)"
          value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
        />
        <input
          type="password" required autoComplete="new-password" minLength={12}
          placeholder="Confirm password"
          value={confirm} onChange={(e) => setConfirm(e.target.value)}
          className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
        />
        {error && <p className="text-sm text-red-700">{error}</p>}
        <button
          type="submit" disabled={submitting}
          className="w-full rounded bg-slate-900 py-2 font-medium text-white disabled:opacity-60 dark:bg-slate-100 dark:text-slate-900"
        >
          {submitting ? 'Creating…' : invite ? 'Accept & continue' : 'Create account'}
        </button>
      </form>
      <p className="mt-4 text-sm text-slate-500">
        Already registered?{' '}
        <Link to="/login" className="underline">Sign in</Link>.
      </p>
    </div>
  );
}
